import { Component, OnInit, OnDestroy } from '@angular/core';
import { AdminService } from '../../services/admin.service';
import { AuthService } from 'src/app/auth.service';
import { CustomToastService } from 'src/app/services/toast.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';
import { ChangePassRequestI } from 'src/app/interface/change-pass-request-i';

@Component({ standalone: false,
  selector: 'app-change-password-modal',
  templateUrl: './change-password-modal.component.html',
  styleUrls: ['./change-password-modal.component.scss']
})
export class ChangePasswordModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly passwordFormatRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

  token!: string;

  currentPassword: string = '';
  newPassword: string = '';
  newPasswordConfirm: string = '';

  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showNewPasswordConfirm: boolean = false;

  currentPasswordError: string = '';
  newPasswordError: string = '';
  newPasswordConfirmError: string = '';

  isSubmitting: boolean = false;

  constructor(
    private adminService: AdminService,
    public modalRef: BsModalRef,
    private authService: AuthService,
    private customToastr: CustomToastService
  ) {}

  ngOnInit(): void {
    this.token = this.authService.getToken() ?? '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Visibility toggles ───────────────────────────────────────────────

  toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleNewPasswordConfirmVisibility(): void {
    this.showNewPasswordConfirm = !this.showNewPasswordConfirm;
  }

  // ── Blur handlers (errors shown only after leaving a field) ──────────

  onCurrentPasswordBlur(): void {
    if (!this.currentPassword) {
      this.currentPasswordError = 'Current password is required.';
    } else {
      this.currentPasswordError = '';
    }
    // re-check new password in case "must differ" rule changed
    if (this.newPassword) this.validateNewPassword();
  }

  onNewPasswordBlur(): void {
    this.validateNewPassword();
    if (!this.newPassword) this.newPasswordError = 'New password is required.';
    if (this.newPasswordConfirm) this.validateConfirmPassword();
  }

  onNewPasswordConfirmBlur(): void {
    this.validateConfirmPassword();
    if (!this.newPasswordConfirm) this.newPasswordConfirmError = 'Please confirm your new password.';
  }

  // ── On-change handler (clears errors once the field becomes valid) ───

  onAnyChange(): void {
    if (this.currentPassword && this.currentPasswordError) {
      this.currentPasswordError = '';
    }
    if (this.newPassword && this.newPasswordError &&
        this.passwordFormatRegex.test(this.newPassword) &&
        this.newPassword !== this.currentPassword) {
      this.newPasswordError = '';
    }
    if (this.newPasswordConfirm && this.newPasswordConfirmError &&
        this.newPasswordConfirm === this.newPassword) {
      this.newPasswordConfirmError = '';
    }
  }

  // ── Private validators ───────────────────────────────────────────────

  private validateNewPassword(): void {
    if (!this.newPassword) {
      this.newPasswordError = '';
      return;
    }
    if (!this.passwordFormatRegex.test(this.newPassword)) {
      this.newPasswordError = 'Must be at least 8 characters and contain uppercase, lowercase, and a number.';
      return;
    }
    if (this.currentPassword && this.newPassword === this.currentPassword) {
      this.newPasswordError = 'New password must be different from the current password.';
      return;
    }
    this.newPasswordError = '';
  }

  private validateConfirmPassword(): void {
    if (!this.newPasswordConfirm) {
      this.newPasswordConfirmError = '';
      return;
    }
    if (this.newPasswordConfirm !== this.newPassword) {
      this.newPasswordConfirmError = 'Passwords do not match.';
      return;
    }
    this.newPasswordConfirmError = '';
  }

  // Runs all validations eagerly on submit, returns true only if clean
  private validateAll(): boolean {
    if (!this.currentPassword) {
      this.currentPasswordError = 'Current password is required.';
    }

    this.validateNewPassword();
    if (!this.newPassword) {
      this.newPasswordError = 'New password is required.';
    }

    this.validateConfirmPassword();
    if (!this.newPasswordConfirm) {
      this.newPasswordConfirmError = 'Please confirm your new password.';
    }

    return !!this.currentPassword &&
      !!this.newPassword &&
      !!this.newPasswordConfirm &&
      !this.currentPasswordError &&
      !this.newPasswordError &&
      !this.newPasswordConfirmError;
  }

  // ── Submit ───────────────────────────────────────────────────────────

  changePassword(): void {
    if (!this.validateAll() || this.isSubmitting) return;

    this.isSubmitting = true;

    const request: ChangePassRequestI = {
      currentPassword: this.currentPassword,
      newPassword: this.newPassword,
      newPasswordConfirm: this.newPasswordConfirm
    };

    const loadingToastRef = this.customToastr.exporting('Please wait while we update your password...', 'Changing Password');

    this.adminService.changePassword(this.token, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          setTimeout(() => {
            if (loadingToastRef) this.customToastr.dismiss(loadingToastRef);

            this.modalRef.hide();

            this.customToastr.downloadComplete(
              "For your security, you'll be redirected to the login page to sign in with your new password.",
              'Password Changed Successfully!'
            );

            this.adminService.logout(); 
          }, 100);
        },
        error: (error) => {
          this.isSubmitting = false;
          setTimeout(() => {
            if (loadingToastRef) this.customToastr.dismiss(loadingToastRef);

            const apiMessage = error?.error?.message
              ? (Array.isArray(error.error.message) ? error.error.message[0] : error.error.message)
              : 'Check your connection and try again, or contact support if the issue persists.';

            this.customToastr.errorCenter(apiMessage, 'Password change failed');
            console.error('Change password failed:', error);
          }, 100);
        }
      });
  }
}
